import React, { useState, Fragment, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  useMediaQuery,
  useTheme,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { styled, alpha } from '@mui/material/styles';
import { Grow } from '@mui/material';

// --- CONSTANTS ---
const primaryBlue = '#0D47A1';
const secondaryBlue = '#1976D2';

// --- STYLED COMPONENTS ---
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(45deg, #ffffff 30%, #f0f4f8 90%)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  borderBottom: `2px solid ${primaryBlue}`,
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.drawer + 1,
  transition: 'top 0.3s ease-in-out',
}));

const StyledToolbar = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: 70,
  padding: '0 24px',
});

const Logo = styled('img')({
  height: '100px',
  width: 'auto',
  maxWidth: '280px',
  cursor: 'pointer',
  userSelect: 'none',
  objectFit: 'contain',
  transition: 'opacity 0.3s ease',
  '&:hover': {
    opacity: 0.8,
  },
});

const NavButton = styled(Button)(({ theme }) => ({
  color: secondaryBlue,
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  padding: '10px 20px',
  borderRadius: '8px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'color 0.3s ease, background-color 0.3s ease',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    width: '0%',
    height: '2px',
    backgroundColor: primaryBlue,
    transition: 'all 0.3s ease-in-out',
    transform: 'translateX(-50%)',
  },
  '&:hover': {
    color: primaryBlue,
    backgroundColor: alpha(primaryBlue, 0.05),
    '&:after': {
      width: 'calc(100% - 40px)',
    },
  },
  whiteSpace: 'nowrap',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: secondaryBlue,
  fontWeight: 500,
  padding: '10px 20px',
  margin: '4px',
  borderRadius: '6px',
  transition: 'background-color 0.2s ease, transform 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(primaryBlue, 0.1),
    color: primaryBlue,
    transform: 'translateX(4px)',
  },
}));

// --- DATA ---
const menuItems = [
  { label: 'Home', href: '#' },
  { label: 'About Us', href: '#about-section' },
  { label: 'Services', href: '#service-section' },
  {
    label: 'Softwares',
    submenu: [
      {
        label: 'Copper Boss',
        submenu: [
          { label: 'Automated Software', href: 'https://copperbosslaptopservice.blogspot.com/2023/01/copper-boss-automated-software-install.html' },
          { label: 'Windows Admin Tools', href: 'https://copperbosslaptopservice.blogspot.com/2023/01/copper-boss-automated-software-install.html' },
          { label: 'IT Softwares', href: 'https://copperbosssupport.blogspot.com/2024/06/copper-boss-all-in-one-it-soluctions.html' },
          { label: 'Software Website', href: 'https://copperbosslaptopservice.blogspot.com/' },
          { label: 'Hardisk Health Software', href: 'https://copperbosslaptopservice.blogspot.com/2022/11/hardisk-health.html' },
          { label: 'Remote Software', href: 'https://copperbosssupport.blogspot.com/2025/06/remote-desktop-software.html' },
          { label: 'K7 Antivirus', href: 'https://copperbosslaptopservice.blogspot.com/2023/03/k7-total-security-antivirus.html' },
        ],
      },
      { label: 'Billing Softwares', href: '#' },
      { label: 'Digital Marketing Apps', href: '#' },
    ],
  },
  {
    label: 'Products',
    submenu: [
      {
        label: 'Accessories',
        submenu: [
          { label: 'Laptop', href: 'https://copperbossmart.blogspot.com/' },
          { label: 'Desktop', href: 'https://copperbossmart.blogspot.com/' },
        ],
      },
      { label: 'PC Build', href: 'https://copperbossmart.blogspot.com/' },
    ],
  },
  {
    label: 'Contact Us',
    submenu: [
      { label: 'Call', href: 'tel:+91-84385-55576' },
      { label: 'WhatsApp', href: 'https://api.whatsapp.com/message/NX2X5TWY3EGKC1?autoload=1&app_absent=0' },
    ],
  },
];

// --- COMPONENTS ---
const DesktopSubMenuItem = ({ item, parentKey, closeToplevelMenu }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = (event) => {
    clearTimeout(timeoutRef.current);
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  };

  const handleMenuClick = () => {
    setAnchorEl(null);
    if (closeToplevelMenu) closeToplevelMenu(true);
  };

  if (!item.submenu) {
    return (
      <StyledMenuItem component="a" href={item.href} onClick={handleMenuClick}>
        {item.label}
      </StyledMenuItem>
    );
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative' }}>
      <StyledMenuItem>
        <span style={{ flexGrow: 1 }}>{item.label}</span>
        <ChevronRightIcon fontSize="small" />
      </StyledMenuItem>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
        TransitionComponent={Grow}
        marginThreshold={0}
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': { pointerEvents: 'auto' }
        }}
        PaperProps={{
          sx: { 
            padding: '8px',
            minWidth: 220,
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: `0 8px 30px ${alpha(primaryBlue, 0.2)}`,
            border: `1px solid ${alpha(primaryBlue, 0.1)}`,
            overflow: 'visible',
            mt: '-8px',
          },
          onMouseEnter: () => clearTimeout(timeoutRef.current),
          onMouseLeave: handleMouseLeave,
        }}
      >
        {item.submenu.map((subItem, index) => (
          <DesktopSubMenuItem 
            key={`${parentKey}-${index}`}
            item={subItem} 
            parentKey={`${parentKey}-${index}`} 
            closeToplevelMenu={handleMenuClick}
          />
        ))}
      </Popover>
    </div>
  );
};

const MobileSubMenu = ({ item, itemKey, openKey, onToggle, handleClose, level = 0 }) => {
  const isOpen = openKey?.startsWith(itemKey);

  const handleClick = () => {
    if (item.submenu) {
      onToggle(isOpen ? (itemKey.lastIndexOf('-') > -1 ? itemKey.substring(0, itemKey.lastIndexOf('-')) : null) : itemKey);
    } else {
      handleClose();
    }
  };

  if (!item.submenu) {
    return (
      <ListItemButton component="a" href={item.href} onClick={handleClick} sx={{ pl: 2 + (level * 2) }}>
        <ListItemText primary={item.label} />
      </ListItemButton>
    );
  }

  return (
    <Fragment>
      <ListItemButton onClick={handleClick} sx={{ pl: 2 + (level * 2) }}>
        <ListItemText primary={item.label} />
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.submenu.map((subItem, index) => (
            <MobileSubMenu 
              key={`${item.label}-${index}`}
              item={subItem} 
              itemKey={`${itemKey}-${index}`}
              openKey={openKey}
              onToggle={onToggle}
              handleClose={handleClose}
              level={level + 1}
            />
          ))}
        </List>
      </Collapse>
    </Fragment>
  );
};

// --- MAIN COMPONENT ---
const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeMenu, setActiveMenu] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const closeTimeoutRef = useRef(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [openMobileMenuKey, setOpenMobileMenuKey] = useState(null);

  const handleDesktopMenuEnter = (event, menu) => {
    clearTimeout(closeTimeoutRef.current);
    setPopoverAnchor(event.currentTarget);
    setActiveMenu(menu);
  };

  const handleDesktopMenuLeave = (immediate = false) => {
    if (immediate) {
      setPopoverAnchor(null);
      setActiveMenu(null);
    } else {
      closeTimeoutRef.current = setTimeout(() => {
        setPopoverAnchor(null);
        setActiveMenu(null);
      }, 300);
    }
  };
  
  const openMobileMenu = (event) => setMobileMenuAnchor(event.currentTarget);
  const closeMobileMenu = () => {
    setMobileMenuAnchor(null);
    setOpenMobileMenuKey(null);
  };
  const handleMobileMenuToggle = (key) => {
    setOpenMobileMenuKey(key === openMobileMenuKey ? null : key);
  };

  return (
    <StyledAppBar>
      <StyledToolbar>
        <Logo 
          src="/images/logo.jpg" 
          alt="Copper Boss Logo"
          onClick={() => window.location.href = '/'}
        />

        {isMobile ? (
          <>
            <IconButton
              edge="end"
              onClick={openMobileMenu}
              sx={{ color: primaryBlue, fontSize: '2rem' }}
            >
              {Boolean(mobileMenuAnchor) ? <CloseIcon fontSize="inherit" /> : <MenuIcon fontSize="inherit" />}
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={closeMobileMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { width: '100%', maxWidth: 360, mt: 1, boxShadow: `0 8px 40px ${alpha(primaryBlue, 0.25)}` } }}
            >
              <List sx={{ color: secondaryBlue, width: '100%' }}>
                {menuItems.map((item, index) => (
                  <MobileSubMenu 
                    key={index} 
                    item={item} 
                    itemKey={`mobile-${index}`}
                    openKey={openMobileMenuKey}
                    onToggle={handleMobileMenuToggle}
                    handleClose={closeMobileMenu} 
                  />
                ))}
              </List>
            </Menu>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: theme.spacing(1) }}>
              {menuItems.map((item, idx) => (
                <div
                  key={`menu-${idx}`}
                  onMouseEnter={(e) => item.submenu && handleDesktopMenuEnter(e, item)}
                  onMouseLeave={() => handleDesktopMenuLeave()}
                  style={{ position: 'relative' }}
                >
                  <NavButton
                    href={item.href}
                    component="a"
                    endIcon={item.submenu && <KeyboardArrowDownIcon />}
                  >
                    {item.label}
                  </NavButton>
                </div>
              ))}
            </div>
            <Popover
              open={Boolean(popoverAnchor)}
              anchorEl={popoverAnchor}
              onClose={() => handleDesktopMenuLeave(true)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              TransitionComponent={Grow}
              disableRestoreFocus
              marginThreshold={0}
              sx={{
                pointerEvents: 'none',
                '& .MuiPaper-root': { pointerEvents: 'auto' }
              }}
              PaperProps={{
                onMouseEnter: () => clearTimeout(closeTimeoutRef.current),
                onMouseLeave: () => handleDesktopMenuLeave(),
                sx: { 
                  padding: '8px',
                  marginTop: '10px',
                  minWidth: 220,
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: `0 8px 30px ${alpha(primaryBlue, 0.2)}`,
                  border: `1px solid ${alpha(primaryBlue, 0.1)}`,
                  overflow: 'visible',
                }
              }}
            >
              {activeMenu?.submenu?.map((subItem, sIdx) => {
                const subKey = `${activeMenu.label}-${sIdx}`;
                return (
                  <DesktopSubMenuItem
                    key={subKey}
                    item={subItem}
                    parentKey={subKey}
                    closeToplevelMenu={() => handleDesktopMenuLeave(true)}
                  />
                );
              })}
            </Popover>
          </>
        )}
      </StyledToolbar>
    </StyledAppBar>
  );
};

export default Header;